// src/pages/ManageBillings.tsx
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../shared/api/axios';
import SidebarAds from './Sidebars/SidebarAds';
import { useTranslation } from 'react-i18next';

const BG = '#050912';
const CARD_BG = '#0D101B';
const BORDER = '#22283a';

type ApiBillingAccount = {
  currency?: string;
  provider?: string;
  display?: {
    brand?: string;
    last4?: string;
    type?: string;
  };
  dailyLimit?: number;
  monthlyLimit?: number;
  hardStop?: boolean;
};

type ApiBillingCampaign = {
  id: string;
  name: string;
  status: string;
  spendTotal: number;
  impressionsTotal: number;
  clicksTotal: number;
};

type AdsSummaryResponse = {
  account: ApiBillingAccount | null;
  campaigns: ApiBillingCampaign[];
};

const ManageBillings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [campaigns, setCampaigns] = useState<ApiBillingCampaign[]>([]);

  const [monthlyLimit, setMonthlyLimit] = useState<number>(100);
  const [currency, setCurrency] = useState<'EUR' | string>('EUR');

  const handleSaveLimit = async () => {
    try {
      const res = await api.patch<ApiBillingAccount>(
        '/billing/limits',
        { monthlyLimit }
      );
      
  
      // If backend returns updated limits:
      if (typeof res.data?.monthlyLimit === 'number') {
        setMonthlyLimit(res.data.monthlyLimit);
      }
  
      alert('Saved');
    } catch (e) {
      console.error('[ManageBillings] Failed to save limit', e);
      alert('Failed to save limit');
    }
  };
  

  // Lifetime totals (computed from backend campaigns)
  const totalLifetimeSpend = useMemo(
    () => campaigns.reduce((sum, c) => sum + (c.spendTotal || 0), 0),
    [campaigns]
  );

  const totalLifetimeImpressions = useMemo(
    () => campaigns.reduce((sum, c) => sum + (c.impressionsTotal || 0), 0),
    [campaigns]
  );

  const totalLifetimeClicks = useMemo(
    () => campaigns.reduce((sum, c) => sum + (c.clicksTotal || 0), 0),
    [campaigns]
  );

  const allTime = useMemo(() => {
    const spend = campaigns.reduce((s, c) => s + (c.spendTotal || 0), 0);
    const impressions = campaigns.reduce((s, c) => s + (c.impressionsTotal || 0), 0);
    const clicks = campaigns.reduce((s, c) => s + (c.clicksTotal || 0), 0);
  
    return {
      monthLabel: t('ad.billing.allTime', 'All time'),
      spend,
      impressions,
      clicks,
    };
  }, [campaigns, t]);
  
  const campaignsForMonth = useMemo(() => {
    // "All time" just shows all campaigns
    return campaigns.map((c) => ({
      id: c.id,
      name: c.name,
      monthKey: 'all',
      spend: c.spendTotal || 0,
      impressions: c.impressionsTotal || 0,
      clicks: c.clicksTotal || 0,
    }));
  }, [campaigns]);
  

  const spendRatio = Math.min(allTime.spend / Math.max(monthlyLimit, 1), 1);

  const handleViewCampaign = (id: string) => {
    navigate(`/advertisement/campaign/${id}`);
  };

  useEffect(() => {
    let cancelled = false;
  
    (async () => {
      try {
        setLoading(true);
        setError(null);
  
        const res = await api.get<AdsSummaryResponse>(
          '/billing/ads-summary'
        );
          
        if (cancelled) return;
  
        setCampaigns(res.data.campaigns || []);
  
        const apiCurrency = res.data.account?.currency || 'EUR';
        setCurrency(apiCurrency);
  
        // If backend has stored limits, use them
        if (typeof res.data.account?.monthlyLimit === 'number') {
          setMonthlyLimit(res.data.account.monthlyLimit);
        }
      } catch (e) {
        console.error('[ManageBillings] Failed to load billing summary', e);
        if (!cancelled) setError('Failed to load billing summary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
  
    return () => {
      cancelled = true;
    };
  }, []);
  
  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: BG,
        color: '#eee',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Sidebar */}
      <SidebarAds />
  
      {/* Main content */}
      <main
        style={{
          marginLeft: 200,
          padding: '24px 32px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 26,
                fontWeight: 600,
                letterSpacing: 0.4,
              }}
            >
              💵 {t('ad.billing.title', 'Manage billings')}
            </h1>
            <p
              style={{
                marginTop: 6,
                marginBottom: 0,
                color: '#9ca3af',
                fontSize: 14,
                maxWidth: 520,
              }}
            >
              {t(
                'ad.billing.subtitle',
                'Set spending limits, review monthly costs, and see how much each campaign consumes.'
              )}
            </p>
          </div>
  
        </header>
  
        {/* Loading / error */}
        {loading ? (
          <div
            style={{
              background: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              padding: 18,
              color: '#9ca3af',
              boxShadow: '0 18px 35px rgba(0,0,0,0.35)',
              marginBottom: 24,
            }}
          >
            {t('ad.billing.loading', 'Loading billing data...')}
          </div>
        ) : error ? (
          <div
            style={{
              background: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              padding: 18,
              color: '#fca5a5',
              boxShadow: '0 18px 35px rgba(0,0,0,0.35)',
              marginBottom: 24,
            }}
          >
            {error}
          </div>
        ) : null}
  
        {/* Top row: limit + “last months” overview */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1.7fr)',
            gap: 20,
            marginBottom: 24,
            opacity: loading ? 0.6 : 1,
            pointerEvents: loading ? 'none' : 'auto',
          }}
        >
          {/* Monthly limit + progress */}
          <div
            style={{
              background: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              padding: 18,
              boxShadow: '0 18px 35px rgba(0,0,0,0.35)',
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                marginBottom: 10,
              }}
            >
              {t('ad.billing.limitTitle', 'Monthly spending limit')}
            </h2>
  
            <p
              style={{
                margin: '4px 0 12px',
                fontSize: 13,
                color: '#9ca3af',
              }}
            >
              {t(
                'ad.billing.limitDescription',
                'Set a soft limit for ad spend this month. We’ll warn you when your campaigns approach this amount.'
              )}
            </p>
  
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 10,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  color: '#9ca3af',
                  minWidth: 90,
                }}
              >
                {t('ad.billing.limitLabel', 'Limit for')} {t('ad.billing.thisPeriod', 'this period')}
              </span>
  
              <input
                type="number"
                min={0}
                step={1}
                value={monthlyLimit}
                onChange={(e) => setMonthlyLimit(Number(e.target.value) || 0)}
                style={{
                  backgroundColor: '#020617',
                  borderRadius: 999,
                  border: '1px solid rgba(148,163,184,0.8)',
                  padding: '8px 10px',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  width: 120,
                }}
              />
  
              <span style={{ fontSize: 13, color: '#9ca3af' }}>
                {currency === 'EUR' ? '€' : currency}
              </span>
  
              {/* Optional: save button (recommended with backend) */}
              <button
  type="button"
  onClick={handleSaveLimit}
  disabled={loading}
  style={{
    marginLeft: 'auto',
    padding: '7px 12px',
    borderRadius: 999,
    border: `1px solid ${BORDER}`,
    background: 'transparent',
    color: '#e5e7eb',
    fontSize: 12,
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.6 : 1,
  }}
>
  {t('ad.billing.saveLimits', 'Save')}
</button>
            </div>
  
            {/* Progress bar */}
            <div style={{ marginTop: 10, marginBottom: 4 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  color: '#9ca3af',
                  marginBottom: 4,
                }}
              >
                <span>
                  {t('ad.billing.currentSpend', 'Current spend')} {t('ad.billing.allTime', 'All time')}
                </span>
                <span>
                  €{allTime.spend.toFixed(2)} / €{monthlyLimit.toFixed(0)}
                </span>
              </div>
              <div
                style={{
                  height: 10,
                  borderRadius: 999,
                  backgroundColor: '#111827',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${spendRatio * 100}%`,
                    height: '100%',
                    background:
                      spendRatio < 0.7
                        ? 'linear-gradient(90deg,#22c55e,#a3e635)'
                        : spendRatio < 1
                        ? 'linear-gradient(90deg,#eab308,#f97316)'
                        : 'linear-gradient(90deg,#ef4444,#b91c1c)',
                    transition: 'width 200ms ease-out',
                  }}
                />
              </div>
            </div>
  
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: '#6b7280',
              }}
            >
              {spendRatio < 0.7 &&
                t('ad.billing.hints.safe', 'Your spend is within a safe range for this month.')}
              {spendRatio >= 0.7 &&
                spendRatio < 1 &&
                t(
                  'ad.billing.hints.approaching',
                  'You are approaching your monthly limit, consider lowering budgets or pausing some campaigns.'
                )}
              {spendRatio >= 1 &&
                t(
                  'ad.billing.hints.exceeded',
                  'You have exceeded your monthly limit. Future campaigns may continue unless you pause them.'
                )}
            </div>
          </div>
  
          {/* “Last months” chart + totals (no dummy data) */}
          <div
            style={{
              background: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              padding: 18,
              boxShadow: '0 18px 35px rgba(0,0,0,0.35)',
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                marginBottom: 10,
              }}
            >
              {t('ad.billing.lastMonthsTitle', 'Last months overview')}
            </h2>
  
            {/* mini bar chart (single real bar until you add monthly rollups) */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 10,
                height: 120,
                marginTop: 8,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    width: '18%',
                    minWidth: 14,
                    borderRadius: 999,
                    background: 'linear-gradient(180deg,#facc15,#b45309)',
                    height: '100%',
                    boxShadow: '0 0 12px rgba(250,204,21,0.55)',
                  }}
                  title={`${t('ad.billing.allTime', 'All time')}: €${allTime.spend.toFixed(2)}`}
                />
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    color: '#9ca3af',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('ad.billing.allTime', 'All time')}
                </div>
              </div>
            </div>
  
            {/* lifetime summary */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 16,
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>
                  {t('ad.billing.lifetimeSpend', 'Lifetime spend')}
                </div>
                <div style={{ fontSize: 20, fontWeight: 600 }}>
                  €{totalLifetimeSpend.toFixed(2)}
                </div>
              </div>
  
              <div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>
                  {t('ad.billing.lifetimeImpressions', 'Total impressions')}
                </div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>
                  {totalLifetimeImpressions.toLocaleString('en-US')}
                </div>
              </div>
  
              <div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>
                  {t('ad.billing.lifetimeClicks', 'Total clicks')}
                </div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>
                  {totalLifetimeClicks.toLocaleString('en-US')}
                </div>
              </div>
            </div>
          </div>
        </section>
  
        {/* Details: stats + campaign breakdown */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1.5fr)',
            gap: 20,
            opacity: loading ? 0.6 : 1,
            pointerEvents: loading ? 'none' : 'auto',
          }}
        >
          {/* Stats (All time for now) */}
          <div
            style={{
              background: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              padding: 18,
              boxShadow: '0 16px 30px rgba(0,0,0,0.35)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                {t('ad.billing.monthlyStatsTitle', 'Monthly statistics')}
              </h2>
  
              {/* kept for layout parity */}
              <div
                style={{
                  fontSize: 13,
                  color: '#9ca3af',
                  padding: '6px 10px',
                  borderRadius: 999,
                  border: '1px solid rgba(148,163,184,0.35)',
                  backgroundColor: '#020617',
                }}
              >
                {t('ad.billing.allTime', 'All time')}
              </div>
            </div>
  
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                <tr>
                  <td style={statLabelCell}>{t('ad.billing.table.spend', 'Spend')}</td>
                  <td style={statValueCell}>€{allTime.spend.toFixed(2)}</td>
                </tr>
                <tr>
                  <td style={statLabelCell}>{t('ad.billing.table.impressions', 'Impressions')}</td>
                  <td style={statValueCell}>{allTime.impressions.toLocaleString('en-US')}</td>
                </tr>
                <tr>
                  <td style={statLabelCell}>{t('ad.billing.table.clicks', 'Clicks')}</td>
                  <td style={statValueCell}>{allTime.clicks.toLocaleString('en-US')}</td>
                </tr>
                <tr>
                  <td style={statLabelCell}>{t('ad.billing.table.cpm', 'Average CPM')}</td>
                  <td style={statValueCell}>
                    {allTime.impressions > 0
                      ? `€${((allTime.spend / allTime.impressions) * 1000).toFixed(2)}`
                      : '–'}
                    <span style={statHint}>
                      {t('ad.billing.table.cpmHint', 'per 1000 impressions')}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={statLabelCell}>{t('ad.billing.table.cpc', 'Average CPC')}</td>
                  <td style={statValueCell}>
                    {allTime.clicks > 0 ? `€${(allTime.spend / allTime.clicks).toFixed(2)}` : '–'}
                    <span style={statHint}>{t('ad.billing.table.cpcHint', 'per click')}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
          {/* Campaign breakdown (All time for now) */}
          <div
            style={{
              background: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              padding: 18,
              boxShadow: '0 16px 30px rgba(0,0,0,0.35)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                {t('ad.billing.campaignBreakdownTitle', 'Campaign breakdown for {{month}}', {
                  month: t('ad.billing.allTime', 'All time'),
                })}
              </h2>
            </div>
  
            {campaignsForMonth.length === 0 ? (
              <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
                {t('ad.billing.campaignBreakdownEmpty', 'No campaigns had spend in this month.')}
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                    <th style={{ textAlign: 'left', padding: '8px 6px', fontWeight: 500 }}>
                      {t('ad.billing.campaignColumns.name', 'Campaign')}
                    </th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', fontWeight: 500 }}>
                      {t('ad.billing.campaignColumns.spend', 'Spend')}
                    </th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', fontWeight: 500 }}>
                      {t('ad.billing.campaignColumns.impressions', 'Impr.')}
                    </th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', fontWeight: 500 }}>
                      {t('ad.billing.campaignColumns.clicks', 'Clicks')}
                    </th>
                    <th style={{ padding: '8px 6px' }} />
                  </tr>
                </thead>
                <tbody>
                  {campaignsForMonth.map((c) => (
                    <tr key={c.id} style={{ borderBottom: `1px solid rgba(31,41,55,0.7)` }}>
                      <td style={{ padding: '8px 6px' }}>{c.name}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>€{c.spend.toFixed(2)}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                        {c.impressions.toLocaleString('en-US')}
                      </td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                        {c.clicks.toLocaleString('en-US')}
                      </td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                        <button
                          onClick={() => handleViewCampaign(c.id)}
                          style={{
                            borderRadius: 999,
                            border: `1px solid ${BORDER}`,
                            background: 'transparent',
                            color: '#e5e7eb',
                            fontSize: 12,
                            padding: '5px 10px',
                            cursor: 'pointer',
                          }}
                        >
                          {t('ad.billing.campaignColumns.view', 'View')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>
    </div>
  );
  
};

const statLabelCell: React.CSSProperties = {
  padding: '8px 6px',
  color: '#9ca3af',
  width: '45%',
};

const statValueCell: React.CSSProperties = {
  padding: '8px 6px',
  textAlign: 'right',
};

const statHint: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: '#6b7280',
  marginTop: 1,
};

export default ManageBillings;
